import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/auth';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import { ACCESS_GROUPS } from './auth/roles';

import Sign from './pages/Sign/sign';
import Home from './pages/Home/home';
import About from './pages/About/about';
import Contact from './pages/Contact/contact';
import Welcome from './pages/Welcome/welcome';
import Forgot from './pages/Forgot/forgot';
import ResetPassword from './pages/ResetPassword/resetPassword';
import Dashboard from './pages/Dashboard/dashboard';

import User from './pages/User/user';
import GetUser from './pages/User/components/GetUser';
import CreateUser from './pages/User/components/CreateUser';
import UpdateUser from './pages/User/components/UpdateUser';
import ShowUser from './pages/User/components/ShowUser';

import Person from './pages/Person/person';
import GetPerson from './pages/Person/components/GetPerson';
import ShowPerson from './pages/Person/components/ShowPerson';
import CreatePerson from './pages/Person/components/CreatePerson';
import UpdatePerson from './pages/Person/components/UpdatePerson';
import GetPersonList from './pages/Person/components/GetPersonList';

import Company from './pages/Company/company';
import GetCompany from './pages/Company/components/GetCompany';
import ShowCompany from './pages/Company/components/ShowCompany';
import CreateCompany from './pages/Company/components/CreateCompany';
import UpdateCompany from './pages/Company/components/UpdateCompany';

import Appointment from './pages/Appointment/appointment';
import GetAppointment from './pages/Appointment/components/GetAppointment';
import CreateAppointment from './pages/Appointment/components/CreateAppointment';
import ShowAppointment from './pages/Appointment/components/ShowAppointment';
import UpdateAppointment from './pages/Appointment/components/UpdateAppointment';
import ConfirmedAppointmentEmail from './pages/Appointment/components/ConfirmedAppointmentEmail';

import Patient from './pages/Patient/patient';
import GetPatient from './pages/Patient/components/GetPatient';
import CreatePatient from './pages/Patient/components/CreatePatient';

import Nutridata from './pages/Nutridata/nutridata';
import GetAllTacoFoods from './pages/Nutridata/components/getAllTacoFoods';
import ShowTacoFood from './pages/Nutridata/components/showTacoFood';
import DynamicFoodDetails from './pages/Nutridata/components/DynamicFoodDetails';

import Anthropometry from './pages/Anthropometry/anthropometry';
import CreateAnthropometry from './pages/Anthropometry/components/CreateAnthropometry';
import GetAnthropometryByPatientId from './pages/Anthropometry/components/GetAnthropometryByPatientId';

import Consultation from './pages/Consultation/consultation';
import GetConsultation from './pages/Consultation/components/GetConsultation';
import CreateConsultation from './pages/Consultation/components/CreateConsultation';
import UpdateConsultation from './pages/Consultation/components/UpdateConsultation';
import GetConsultationByPatientId from './pages/Consultation/components/GetConsultationByPatientId';
import ShowConsultation from './pages/Consultation/components/ShowConsultation';

import Diet from './pages/Diet/diet';
import CreateDiet from './pages/Diet/components/CreateDiet';
import UpdateDiet from './pages/Diet/components/UpdateDiet';
import GetDietByPatientId from './pages/Diet/components/GetDiet';
import GetTaco4Foods from './pages/Nutridata/components/getTaco4Foods';
import ShowTaco4Foods from './pages/Nutridata/components/showTaco4Foods';
import GetAnthropometryWithFilters from './pages/Anthropometry/components/GetAntropometryWithFilters';

import GetNutritionist from './pages/Nutritionist/components/GetNutritionist';
import Nutritionist from './pages/Nutritionist/nutritionist';
import CreateNutritionist from './pages/Nutritionist/components/CreateNutritionist';
import UpdatePatient from './pages/Patient/components/UpdatePatient';
import UpdateNutritionist from './pages/Nutritionist/components/UpdateNutritionist';
import ShowPatient from './pages/Patient/components/ShowPatient';
import ShowNutritionist from './pages/Nutritionist/components/ShowNutritionist';

import CustomFood from './pages/CustomFood/customFood';
import CreateCustomFood from './pages/CustomFood/components/CreateCustomFood';

import Setup from './pages/Setups/setup';
import GetConsultationTypes from './pages/Setups/ConsultationTypes/GetConsultationTypesPage';
import GetUnityMeasurements from './pages/Setups/UnityMeasurements/UnityMeasurementsPage';
import Consumer from './pages/Consumer/consumer';
import OnBoarding from './pages/Consumer/components/OnBoarding';

const AccessControl0 = ["Developer"];
const AccessControl1 = ["Developer", "Admin"];
const AccessControl2 = ["Developer", "Admin", "Manager"];
const AccessControl3 = ["Developer", "Admin", "Manager", "Coordinator"];
const AccessControl4 = ["Developer", "Admin", "Manager", "Coordinator", "User"];


const AppRouter = () => {

  const RouteWrapper = ({ children }) => {
    const location = useLocation();

    useEffect(() => {
      // Substitui a URL atual sempre que a rota mudar
      window.history.replaceState(null, '', '/nutricaoapp');
    }, [location]);

    return children;
  };

  return (
    <Router>
      <AuthProvider>
        {/* <RouteWrapper> */}
        <Routes>
          {/* Public Routes */}
          <Route exact path="/" element={<Welcome />} />
          <Route exact path="/sign" element={<Sign />} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/resetPassword" element={<ResetPassword />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="home/appointment/confirmed_appointment" element={<ConfirmedAppointmentEmail />} />

          {/* Protected Routes */}
          <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>}>
            <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

            {/* Consumer */}
            <Route path="consumer" element={<ProtectedRoute><Consumer /></ProtectedRoute>}>
              <Route path="onboarding_consumer" element={<ProtectedRoute role={AccessControl3}> <OnBoarding /></ProtectedRoute>} />
            </Route>

            {/* Anthropometry Routes */}
            <Route path="anthropometry" element={<ProtectedRoute><Anthropometry /></ProtectedRoute>}>
              <Route path="create" element={<ProtectedRoute role={AccessControl3}> <CreateAnthropometry /></ProtectedRoute>} />
              <Route path="get" element={<ProtectedRoute role={AccessControl4}><GetAnthropometryByPatientId /></ProtectedRoute>} />
              <Route path="get/withFilters" element={<ProtectedRoute role={AccessControl4}><GetAnthropometryWithFilters /></ProtectedRoute>} />
            </Route>

            {/* Appointment Routes */}
            <Route path="appointment" element={<ProtectedRoute><Appointment /></ProtectedRoute>} >
              <Route path="get" element={<ProtectedRoute> <GetAppointment /></ProtectedRoute>} />
              <Route path="create" element={<ProtectedRoute role={AccessControl4}> <CreateAppointment /></ProtectedRoute>} />
              <Route path="show/:appointment_id" element={<ProtectedRoute role={AccessControl4}> <ShowAppointment /></ProtectedRoute>} />
              <Route path="update/:appointment_id" element={<ProtectedRoute role={AccessControl4}> <UpdateAppointment /></ProtectedRoute>} />
            </Route>


            {/* Company Routes */}
            <Route path="company" element={<ProtectedRoute><Company /></ProtectedRoute>}>
              <Route path="get" element={<ProtectedRoute><GetCompany /></ProtectedRoute>} />
              <Route path="show/:company_id" element={<ProtectedRoute><ShowCompany /></ProtectedRoute>} />
              <Route path="create" element={<ProtectedRoute role={AccessControl3}><CreateCompany /></ProtectedRoute>} />
              <Route path="update/:company_id" element={<ProtectedRoute role={AccessControl2}><UpdateCompany /></ProtectedRoute>} />
            </Route>

            {/* Consultation Routes */}
            <Route path="consultation" element={<ProtectedRoute><Consultation /></ProtectedRoute>}>
              <Route path="get" element={<ProtectedRoute role={AccessControl4}><GetConsultation /></ProtectedRoute>} />
              <Route path="get/byPatient" element={<ProtectedRoute role={AccessControl4}><GetConsultationByPatientId /></ProtectedRoute>} />
              <Route path="show/:consultation_id" element={<ProtectedRoute role={AccessControl1}> <ShowConsultation /></ProtectedRoute>} />
              <Route path="create/:appointment_id" element={<ProtectedRoute role={AccessControl1}> <CreateConsultation /></ProtectedRoute>} />
              <Route path="update/:consultation_id" element={<ProtectedRoute role={AccessControl1}> <UpdateConsultation /></ProtectedRoute>} />
            </Route>

            {/* Diet Routes */}
            <Route path="diet" element={<ProtectedRoute><Diet /></ProtectedRoute>}>
              <Route path="create" element={<ProtectedRoute role={AccessControl4}><CreateDiet /></ProtectedRoute>} />
              <Route path="get" element={<ProtectedRoute role={AccessControl4}><GetDietByPatientId /></ProtectedRoute>} />
              <Route path="update/:diet_id" element={<ProtectedRoute role={AccessControl4}><UpdateDiet /></ProtectedRoute>} />
            </Route>

            {/* Patient Routes */}
            <Route path="patient" element={<ProtectedRoute><Patient /></ProtectedRoute>}>
              <Route path="get" element={<ProtectedRoute><GetPatient /></ProtectedRoute>} />
              <Route path="create" element={<ProtectedRoute role={AccessControl4}><CreatePatient /></ProtectedRoute>} />
              <Route path="update/:person_id" element={<ProtectedRoute role={AccessControl4}><UpdatePatient /></ProtectedRoute>} />
              <Route path="show/:person_id" element={<ProtectedRoute role={AccessControl4}><ShowPatient /></ProtectedRoute>} />
            </Route>

            {/* Nutritionist Routes */}
            <Route path="nutritionist" element={<ProtectedRoute><Nutritionist /></ProtectedRoute>}>
              <Route path="get" element={<ProtectedRoute><GetNutritionist /></ProtectedRoute>} />
              <Route path="create" element={<ProtectedRoute role={AccessControl4}><CreateNutritionist /></ProtectedRoute>} />
              <Route path="update/:person_id" element={<ProtectedRoute role={AccessControl4}><UpdateNutritionist /></ProtectedRoute>} />
              <Route path="show/:person_id" element={<ProtectedRoute role={AccessControl4}><ShowNutritionist /></ProtectedRoute>} />
            </Route>


            {/* Person Routes */}
            <Route path="person" element={<ProtectedRoute><Person /></ProtectedRoute>}>
              <Route path="get" element={<ProtectedRoute><GetPerson /></ProtectedRoute>} />
              <Route path="show/:person_id" element={<ProtectedRoute role={AccessControl4}><ShowPerson /></ProtectedRoute>} />
              <Route path="create" element={<ProtectedRoute role={AccessControl4}><CreatePerson /></ProtectedRoute>} />
              <Route path="update/:person_id" element={<ProtectedRoute role={AccessControl3}><UpdatePerson /></ProtectedRoute>} />
              <Route path="get/list" element={<ProtectedRoute role={AccessControl4}><GetPersonList /></ProtectedRoute>} />
            </Route>


            {/* TacoFoods */}
            <Route path="nutridata" element={<ProtectedRoute><Nutridata /></ProtectedRoute>} >
              <Route path="get" element={<ProtectedRoute> <GetAllTacoFoods /></ProtectedRoute>} />
              <Route path="get/atfd" element={<ProtectedRoute> <GetAllTacoFoods /></ProtectedRoute>} />
              <Route path="get/dynamic" element={<ProtectedRoute role={AccessControl3}> <DynamicFoodDetails /></ProtectedRoute>} />
              <Route path="create" element={<ProtectedRoute role={AccessControl3}> <ShowTacoFood /></ProtectedRoute>} />
              <Route path="show/:food_id" element={<ProtectedRoute role={AccessControl4}> <ShowTacoFood /></ProtectedRoute>} />
              <Route path="update/:food_id" element={<ProtectedRoute role={AccessControl3}> <ShowTacoFood /></ProtectedRoute>} />
              <Route path="get/taco4foods" element={<ProtectedRoute> <GetTaco4Foods /></ProtectedRoute>} />
              <Route path="show/taco4foods/:food_id" element={<ProtectedRoute role={AccessControl4}> <ShowTaco4Foods /></ProtectedRoute>} />
            </Route>

            {/* Custom Foods */}
            <Route path="custom_food" element={<ProtectedRoute><CustomFood /></ProtectedRoute>} >
              <Route path="create" element={<ProtectedRoute role={AccessControl3}> <CreateCustomFood /></ProtectedRoute>} />
            </Route>


            {/* Setup Routes */}
            <Route path="setup" element={<ProtectedRoute><Setup /></ProtectedRoute>}>
              <Route path="consultation_types/get" element={<ProtectedRoute><GetConsultationTypes /></ProtectedRoute>} />
              <Route path="unity_measurements/get" element={<ProtectedRoute><GetUnityMeasurements /></ProtectedRoute>} />
            </Route>


            {/* User Routes */}
            <Route path="user" element={<ProtectedRoute><User /></ProtectedRoute>}>
              <Route path="get" element={<ProtectedRoute><GetUser /></ProtectedRoute>} />
              <Route path="show/:user_id" element={<ProtectedRoute><ShowUser /></ProtectedRoute>} />
              <Route path="create" element={<ProtectedRoute role={AccessControl4}><CreateUser /></ProtectedRoute>} />
              <Route path="update/:user_id" element={<ProtectedRoute role={AccessControl2}><UpdateUser /></ProtectedRoute>} />
            </Route>
          </Route>
          {/* Catch-All Route */}
          <Route path="*" element={<Navigate to="/home" />} />
        </Routes>
        {/* </RouteWrapper> */}
      </AuthProvider>
    </Router>
  );
};

export default AppRouter;
